import { CuboidPartsArrays } from '@App/app/entities/layer/cuboid.model';
import {
  CUBOID_PART,
  CUBOID_PART_STRICT,
  LAYER_TYPES,
  RECT_TUBE_PART,
  RECT_TUBE_STRICT,
} from '@App/app/entities/layer/enums/layer-types.enum';
import { RectTubePartsArrays } from '@App/app/entities/layer/rect-tube.model';
import { inject, Injectable } from '@angular/core';
import { Mesh, Vector3 } from 'babylonjs';
import { Subject } from 'rxjs';
import { disposeMeshesInPlace } from '../../../../helpers/layers-helpers';
import { UtilsService } from '../../../utils-service/utils.service';
import { RectangleToolsAzimuthService } from './rectangle-tools-azimuth.service';
import { OrientedBoxSaveParams, RectangleToolsSaveService } from './rectangle-tools-save.service';
import { RectangleToolsUtilsService } from './rectangle-tools-utls.service';

@Injectable({
  providedIn: 'root',
})
export abstract class RectangleToolsService {
  samples: CuboidPartsArrays<Mesh | null> | RectTubePartsArrays<Mesh | null>;
  activePart: CUBOID_PART | RECT_TUBE_PART;
  highlightedSample: {
    part: CUBOID_PART | RECT_TUBE_PART;
    i: number;
  };

  protected _dataChange$ = new Subject<void>();
  dataChange$ = this._dataChange$.asObservable();

  private _rectangleToolsUtilsService = inject(RectangleToolsUtilsService);
  private _utilsService = inject(UtilsService);
  private _rectangleToolsSaveService = inject(RectangleToolsSaveService);
  private _rectangleToolsAzimuthService = inject(RectangleToolsAzimuthService);

  onClickAction() {
    if (this.activePart) {
      const hit = this._utilsService.pickRay();
      if (hit && hit.pickedPoint) {
        this.createSamplePoint(hit.pickedPoint.clone());
        this._dataChange$.next();
      }
    }
  }

  save(params: OrientedBoxSaveParams) {
    this._rectangleToolsSaveService.save(params);
  }

  highlightSample(part: CUBOID_PART_STRICT | RECT_TUBE_STRICT, i: number) {
    this.highlightSamplePoint(part, i);
    this._dataChange$.next();
  }

  deleteSample(part: CUBOID_PART_STRICT | RECT_TUBE_STRICT, i: number) {
    this.deleteSamplePoint(part, i);
    this._dataChange$.next();
  }

  clearSamples() {
    this.clearSamplesPoints();
    this._dataChange$.next();
  }

  protected highlightSamplePoint(part: CUBOID_PART_STRICT | RECT_TUBE_STRICT, i: number) {
    if (this.highlightedSample.part && this.highlightedSample.i > -1) {
      this._rectangleToolsUtilsService.changeSampleMaterial(
        this.samples[this.highlightedSample.part][this.highlightedSample.i],
        true,
      );
    }
    if (this.highlightedSample.part === part && this.highlightedSample.i === i) {
      this.highlightedSample = { part: CUBOID_PART.NONE, i: -1 };
    } else {
      this._rectangleToolsUtilsService.changeSampleMaterial(this.samples[part][i], false);
      this.highlightedSample = { part, i };
    }
  }

  protected deleteSamplePoint(part: CUBOID_PART_STRICT | RECT_TUBE_STRICT, i: number) {
    this.samples[part][i]?.dispose();
    this.samples[part][i] = null;
    this.samples[part] = this.samples[part].filter((_, j) => j !== i);
    if (this.highlightedSample.part === part && this.highlightedSample.i === i) {
      this.highlightedSample = { part: CUBOID_PART.NONE, i: -1 };
    }
  }

  protected clearSamplesPoints() {
    this.activePart = CUBOID_PART.NONE;
    if (this.samples) {
      for (const key in this.samples) {
        if (Object.prototype.hasOwnProperty.call(this.samples, key)) {
          const part = (key as unknown) as CUBOID_PART_STRICT | RECT_TUBE_STRICT;
          if (this.samples[part]?.length) {
            this.removeSamplesOfPart(part);
          }
        }
      }
      this.samples = { 1: [], 2: [], 3: [] };
    }
    this.highlightedSample = { part: CUBOID_PART.NONE, i: -1 };
  }

  findAzimuthAndVerticalTilt(type: LAYER_TYPES.RECT_TUBE | LAYER_TYPES.CUBOID) {
    this._rectangleToolsAzimuthService.findAzimuthAndVerticalTilt(type);
  }

  calculateAzimuthAndVerticalTilt(mesh: Mesh, modelCenter?: Vector3): [number, number] {
    return this._rectangleToolsSaveService.calculateAzimuthAndVerticalTilt(mesh, modelCenter);
  }

  createSamplePoint(position: Vector3) {
    const sphere = this._rectangleToolsUtilsService.createPoint(position);
    if (this.activePart) {
      this.samples[this.activePart].push(sphere);
    }
  }

  private removeSamplesOfPart(part: CUBOID_PART_STRICT | RECT_TUBE_STRICT) {
    disposeMeshesInPlace(this.samples[part]);
  }
}
